import { t } from 'i18next';
import { z } from 'zod';

import { QR_CODE_ERROR_CORRECTION_LEVELS } from '~/constants/qr-code';
import { DynamicQrCodeData, StaticQrCodeData, UpdateQrCodeDataPayload } from '~/types/qrCode';
import { getGeneralBuilderSchema } from '~/utils/validation/qrCodeBuilder';

export const getAreQRCodeStylesChanged = (qrCodeData: DynamicQrCodeData | StaticQrCodeData) => {
  if (Object.keys(qrCodeData).length === 0) {
    return false;
  }

  const { style, embeddedLogo } = qrCodeData;

  if (!style) {
    return false;
  }

  switch (true) {
    case !!embeddedLogo:
    case style.errorCorrectionLevel !== QR_CODE_ERROR_CORRECTION_LEVELS.Q:
    case style.cornerBorder.style !== 1:
    case style.cornerBorder.color !== '#000000':
    case style.cornerCenter.style !== 16:
    case style.cornerCenter.color !== '#000000':
    case style.frame.style !== 34:
    case style.frame.color !== '#000000':
    case style.frame.phrase !== 'Scan me!':
    case style.frame.phraseColor !== '#ffffff':
    case style.shape.style !== 'square':
    case style.shape.color !== '#000000':
    case style.shape.transparentBg:
    case style.shape.backgroundColor !== '#ffffff':
      return true;
    default:
      return false;
  }
};

const getLocationItem = (value: string, nextValue?: string) => {
  if (value && nextValue) {
    return `${value}, `;
  }

  return value || '';
};
// TODO add location type
export const getDefaultAddress = (location: any) => {
  if (!location) return '';

  return `${getLocationItem(location?.street, location?.number)}${getLocationItem(location?.number, location?.postalCode)}${getLocationItem(location?.postalCode, location?.city)}${getLocationItem(location?.city, location?.country)}${getLocationItem(location?.country)}`;
};

const generalBuilderSchema = getGeneralBuilderSchema({ t });
type GeneralBuilderSchema = z.infer<typeof generalBuilderSchema>;

export const getUpdateDynamicQrCodePayload = ({
  values,
  qrCodeData,
}: {
  values: GeneralBuilderSchema;
  qrCodeData?: DynamicQrCodeData;
}): UpdateQrCodeDataPayload => {
  if (!qrCodeData) return {};

  const qrCodeDataPayload: UpdateQrCodeDataPayload = {};

  if (values.qrCodeName !== qrCodeData?.name) {
    qrCodeDataPayload.name = values.qrCodeName;
  }

  if (values.settings.qrCodeUrl !== qrCodeData?.uri) {
    qrCodeDataPayload.uri = values.settings.qrCodeUrl;
  }

  if (values.settings.isScanLimitsEnabled) {
    if (values.settings.scanLimit && values.settings.scanLimit !== qrCodeData?.scanLimit) {
      qrCodeDataPayload.scan_limit = values.settings.scanLimit;
    }
  } else {
    if (qrCodeData.scanLimit) {
      qrCodeDataPayload.scan_limit = null;
    }
  }

  if (values.settings.isAutoGeneratedUrl !== qrCodeData?.isAutogeneratedUrl) {
    qrCodeDataPayload.is_autogenerated_url = values.settings.isAutoGeneratedUrl;
  }

  if (values.qrCodeFolder && (!qrCodeData?.folder?.id || values.qrCodeFolder !== qrCodeData.folder.id)) {
    qrCodeDataPayload.folder_id = values.qrCodeFolder;
  }

  if (values.passwordProtection.password) {
    qrCodeDataPayload.password = values.passwordProtection.password;
  }

  if (values.passwordProtection.isPasswordProtected !== qrCodeData.isPasswordProtected) {
    qrCodeDataPayload.isPasswordProtected = values.passwordProtection.isPasswordProtected;
  }

  return qrCodeDataPayload;
};
